import isEqual from 'lodash/isEqual';
import { Howl } from 'howler';
import { AlertPriority } from '@ixicare/resources';

export const NotificationSounds = {
  [AlertPriority.Critical]: 'alert_critical.mp3',
  [AlertPriority.Informative]: 'alert_informative.mp3',
  [AlertPriority.Manual]: 'alert_manual.mp3',
  [AlertPriority.Urgent]: 'alert_urgent.mp3',
  [AlertPriority.Magenta]: 'alert_devices_critical.mp3',
  [AlertPriority.Purple]: 'alert_devices_non_critical.mp3',
};

export const DEFAULT_MUTE_REQUEST_ID = '1234';

/** Reserved muteRequests id reflecting the user's manual presence state.
 *  Pushed by an external reaction (see Presence store wiring in app init)
 *  so SoundService stays free of store coupling. */
export const PRESENCE_MUTE_REQUEST_ID = 'presence';

class SoundService {
  Types = AlertPriority;

  private activeSoundTypes: AlertPriority[] = [];

  private playingSoundType: AlertPriority | undefined;
  private playingSound: Howl | undefined;

  // The initial mute request is so that the user has to interact (browser restrictions)
  public muteRequests: string[] = [DEFAULT_MUTE_REQUEST_ID];

  private soundCache = new Map<AlertPriority, Howl>();

  private muteListeners = new Set<() => void>();

  /** Subscribe to mute-state changes (any cause: user click, presence,
   *  auto-play unlock, reset). Returns an unsubscribe fn. */
  public subscribe(listener: () => void): () => void {
    this.muteListeners.add(listener);
    return () => this.muteListeners.delete(listener);
  }

  private emitMuteChange() {
    this.muteListeners.forEach((cb) => cb());
  }

  canAutoPlay = (() => {
    if (typeof window === 'undefined') return Promise.resolve(false);
    return import('can-autoplay')
      .then((module) => module.default.audio())
      .then(({ result }) => {
        if (result) {
          this.muteRequests = [];
          this.emitMuteChange();
        }
        return result;
      });
  })();

  updateActiveSoundTypes(updatedSoundTypes: AlertPriority[]) {
    console.info('SoundService', 'update', updatedSoundTypes.join(','));
    if (isEqual(this.activeSoundTypes, updatedSoundTypes)) return;
    this.activeSoundTypes = updatedSoundTypes;

    this.start();
  }

  public start = () => {
    console.info('SoundService', 'start', !this.muteRequests.length ? 'allowed' : 'declined');
    if (this.muteRequests.length) return;

    const soundTypeToPlay = this.activeSoundTypes.find((a) => a);

    // If were still playing the same sound, check if it hasn't stagnated
    if (this.playingSound && !this.playingSound.playing() && this.playingSoundType === soundTypeToPlay) {
      this.playingSound.play();
    }

    if (soundTypeToPlay !== this.playingSoundType) {
      this.playingSound?.stop();
      this.playingSound = undefined;
      this.playingSoundType = soundTypeToPlay;

      if (soundTypeToPlay) {
        if (this.soundCache.has(soundTypeToPlay)) {
          this.playingSound = this.soundCache.get(soundTypeToPlay);
          this.playingSound?.play();
        } else {
          this.playingSound = new Howl({
            src: `/assets/sounds/${NotificationSounds[soundTypeToPlay]}`,
            autoplay: true,
            loop: true,
          });
          this.playingSound.on('load', this.handleSoundLoaded);
          this.soundCache.set(soundTypeToPlay, this.playingSound);
        }
      }
    }
  };

  private handleSoundLoaded = (error: any) => {
    if (error) {
      console.error('SoundService failed to load sound', error);
      this.playingSound?.unload();
      if (this.playingSoundType) {
        this.soundCache.delete(this.playingSoundType);
      }
      this.playingSound = undefined;
      this.playingSoundType = undefined;
      return;
    }
    if (!this.playingSound) return;
    console.info('SoundService', 'load', this.playingSoundType);
  };

  public stop() {
    console.info('SoundService', 'stop');
    if (this.playingSound) {
      this.playingSound.stop();
    }
    this.playingSoundType = undefined;
    this.playingSound = undefined;
  }

  public reset() {
    console.info('SoundService', 'reset');
    this.stop();
    this.activeSoundTypes = [];
    this.muteRequests = [];
    this.emitMuteChange();
  }

  public mute() {
    this.stop();
    const id = Math.round(Math.random() * 1e6).toString();
    console.info('SoundService', 'mute', id);
    this.muteRequests.push(id);
    this.emitMuteChange();
    return id;
  }

  public unmute(id: string) {
    if (this.muteRequests.includes(id)) {
      console.info('SoundService', 'unmute', id);
      this.muteRequests = this.muteRequests.filter((mr) => mr !== id);
      this.emitMuteChange();
      this.start();
    }
  }

  /** Tie sound output to the user's presence silence state. Called by a
   *  mobx reaction in the dashboard's app init; idempotent. */
  public setPresenceSilenced(silenced: boolean) {
    const has = this.muteRequests.includes(PRESENCE_MUTE_REQUEST_ID);
    if (silenced && !has) {
      this.muteRequests.push(PRESENCE_MUTE_REQUEST_ID);
      this.emitMuteChange();
      this.stop();
    } else if (!silenced && has) {
      this.muteRequests = this.muteRequests.filter((mr) => mr !== PRESENCE_MUTE_REQUEST_ID);
      this.emitMuteChange();
      this.start();
    }
  }
}

export default new SoundService();
